// Decision Rooms phase view — Phase 1 scaffold (2026-04-26).
//
// Lists every Decision Room owned by the rep. Detail view, MAP, docs,
// and the public /space/<slug> surface land in subsequent commits.
// For now this gives the new dashboard nav item a real (data-backed)
// destination instead of a 404.
//
// Auth via the same Bearer token everything else uses. API endpoint:
// GET /api/buyer-spaces (returns { buyerSpaces: [...] }).

import { useEffect, useMemo, useState } from 'react';
import { parseDeepLink } from '../utils/deepLink';
import BuyerSpaceDetail from './BuyerSpaceDetail';

// Same fallback the rest of the app uses so this component works
// when REACT_APP_API_URL isn't set in Vercel — without it, fetch
// hits the SPA's own domain and returns index.html, blowing up the
// JSON parse with "Unexpected token '<'".
const API_BASE = process.env.REACT_APP_API_URL || 'https://trailercst-backend-production.up.railway.app';

function fmtDate(s) {
  if (!s) return '';
  try { return new Date(s).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }); }
  catch { return s; }
}

function fmtRelative(s) {
  if (!s) return 'never';
  const ms = Date.now() - new Date(s).getTime();
  if (ms < 60_000) return 'just now';
  if (ms < 3_600_000) return `${Math.round(ms / 60_000)}m ago`;
  if (ms < 86_400_000) return `${Math.round(ms / 3_600_000)}h ago`;
  return `${Math.round(ms / 86_400_000)}d ago`;
}

function statusChip(status) {
  const map = {
    open: { label: 'Open', bg: 'rgba(34, 197, 94, 0.15)', fg: '#16a34a' },
    won: { label: 'Won', bg: 'rgba(168, 85, 247, 0.15)', fg: '#9333ea' },
    lost: { label: 'Lost', bg: 'rgba(239, 68, 68, 0.15)', fg: '#dc2626' },
    dormant: { label: 'Dormant', bg: 'rgba(107, 114, 128, 0.15)', fg: '#4b5563' },
  };
  const cfg = map[status] || map.open;
  return (
    <span style={{
      display: 'inline-block', padding: '2px 10px', borderRadius: 100,
      fontSize: 11, fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase',
      background: cfg.bg, color: cfg.fg,
    }}>
      {cfg.label}
    </span>
  );
}

// Parse the URL hash for a `#/buyer-spaces/<spaceId>[/<sectionId>]`
// subpath. Returns { spaceId, sectionId } where either can be null.
// Centralized so both the initializer + popstate listener stay in
// sync. The phase token itself ('buyer-spaces') is owned by App.jsx;
// here we only care about what comes after.
function parseSpaceHash() {
  if (typeof window === 'undefined') return { spaceId: null, sectionId: null, docId: null };
  // Shared parser (2026-08-23). The hand-rolled split used to return
  // 'documents?doc=abc-123' as the section id, which matched no known
  // section — so an emailed link to a specific document opened the room
  // on Welcome instead.
  const intent = parseDeepLink(window.location.hash);
  if (intent.phase !== 'buyer-spaces') return { spaceId: null, sectionId: null, docId: null };
  return { spaceId: intent.spaceId, sectionId: intent.sectionId, docId: intent.docId };
}

export default function BuyerSpacesPhase({ authToken, setPhase, onOpenDemo, sellerName, currentUserId, isCompanyAdmin, uiFlags }) {
  const [spaces, setSpaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // selectedSpaceId hydrates from the URL hash so a refresh on a
  // detail-view URL stays on that Space instead of bouncing back to
  // the dashboard. Hash format: #/buyer-spaces/<spaceId>/<sectionId>.
  const [selectedSpaceId, setSelectedSpaceId] = useState(() => parseSpaceHash().spaceId);
  const [createOpen, setCreateOpen] = useState(false);

  // After a manual create: drop the new room into the list optimistically
  // (it has 0 trailers / share links until the rep adds them) and jump
  // straight into its detail view so they can start curating.
  const handleCreated = (newSpace) => {
    if (!newSpace || !newSpace.id) return;
    setSpaces((prev) => [
      { ...newSpace, reelCount: 0, distributionCount: 0, ownedByCaller: true, ownerName: null },
      ...prev.filter((s) => s.id !== newSpace.id),
    ]);
    setCreateOpen(false);
    setSelectedSpaceId(newSpace.id);
  };

  // Listen for browser back/forward — keeps in-app state synced
  // with the URL when the user uses the back button.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onPop = () => {
      const { spaceId } = parseSpaceHash();
      setSelectedSpaceId(spaceId);
    };
    window.addEventListener('popstate', onPop);
    window.addEventListener('hashchange', onPop);
    return () => {
      window.removeEventListener('popstate', onPop);
      window.removeEventListener('hashchange', onPop);
    };
  }, []);

  // Whenever the seller picks/leaves a Space, write it back to the
  // hash so a refresh restores the same view. We replaceState (not
  // pushState) so the browser history doesn't fill up with one entry
  // per nav click — refresh works, and Back still walks real
  // navigations.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const current = window.location.hash;
    if (selectedSpaceId) {
      // Preserve any existing section subpath (set by BuyerSpaceDetail)
      // so we don't strip it on re-render. Only overwrite the spaceId.
      const { sectionId } = parseSpaceHash();
      const target = sectionId
        ? `#/buyer-spaces/${selectedSpaceId}/${sectionId}`
        : `#/buyer-spaces/${selectedSpaceId}`;
      if (current !== target) {
        window.history.replaceState(null, '', window.location.pathname + window.location.search + target);
      }
    } else {
      const target = '#/buyer-spaces';
      if (current !== target) {
        window.history.replaceState(null, '', window.location.pathname + window.location.search + target);
      }
    }
  }, [selectedSpaceId]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetch(`${API_BASE}/api/buyer-spaces`, {
      headers: { Authorization: `Bearer ${authToken}` },
    })
      .then(async (r) => {
        if (!r.ok) {
          const body = await r.json().catch(() => ({}));
          throw new Error(body.error || `Failed to load (status ${r.status})`);
        }
        return r.json();
      })
      .then((data) => {
        if (cancelled) return;
        setSpaces(Array.isArray(data.buyerSpaces) ? data.buyerSpaces : []);
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err.message);
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, [authToken]);

  // Roll-up stats across all Spaces — reads punchier than just a list
  // header, and earns its space in the visual budget by giving the
  // seller a one-glance read on portfolio scale. MUST stay above the
  // early return below (Rules of Hooks: React requires hooks called
  // in the same order on every render).
  const summary = useMemo(() => {
    const open = spaces.filter((s) => s.status === 'open').length;
    const won = spaces.filter((s) => s.status === 'won').length;
    const totalReels = spaces.reduce((n, s) => n + (s.reelCount || 0), 0);
    const totalDists = spaces.reduce((n, s) => n + (s.distributionCount || 0), 0);
    return { open, won, totalReels, totalDists };
  }, [spaces]);

  // Detail view takes over the whole phase canvas when a Space is open.
  // Back button drops the seller back to the list (state reset) — does
  // NOT navigate away from the buyer-spaces phase, so the nav item stays
  // active in the left sidebar.
  if (selectedSpaceId) {
    return (
      <BuyerSpaceDetail
        authToken={authToken}
        spaceId={selectedSpaceId}
        onBack={() => setSelectedSpaceId(null)}
        onOpenDemo={onOpenDemo}
        sellerName={sellerName}
        currentUserId={currentUserId}
        isCompanyAdmin={isCompanyAdmin}
        uiFlags={uiFlags}
      />
    );
  }

  return (
    <div style={{
      // 2026-05-08: dropped the per-phase gradient backdrop. With the
      // edge-to-edge layout the gradient was confined to the inner
      // 1600 container, leaving a visible color seam against the page
      // background. Uniform page background is the cleaner read.
      // (If we want the gradient back later, mount it on
      // body[data-phase="buyer-spaces"] in App.css so it covers the
      // whole viewport.)
      minHeight: '100vh',
      color: 'var(--room-text-strong)',
    }}>
      <div style={{ maxWidth: 1600, margin: '0 auto', padding: '40px 24px 80px' }}>
        {/* Hero strip — title, subtitle, back button, and the four
            roll-up stats. The stats render as flat tiles to read at
            a glance without competing with the cards below. */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 24, marginBottom: 24, flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 280 }}>
            <h1 style={{
              margin: 0, fontSize: 36, fontWeight: 700, letterSpacing: '-0.02em',
              background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 50%, #a855f7 100%)',
              WebkitBackgroundClip: 'text', backgroundClip: 'text',
              WebkitTextFillColor: 'transparent', color: 'transparent',
            }}>
              Decision Rooms
            </h1>
            <p style={{ margin: '8px 0 0', color: 'var(--room-text-muted)', fontSize: 15, maxWidth: 560 }}>
              One branded workspace per opportunity. Every trailer, conversation,
              document, and stakeholder grouped where the deal lives.
            </p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={() => setCreateOpen(true)}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 8,
                padding: '10px 18px', borderRadius: 10, border: 'none',
                background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer',
                fontFamily: 'inherit', boxShadow: '0 2px 8px rgba(124, 58, 237, 0.3)',
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <line x1="12" y1="5" x2="12" y2="19" />
                <line x1="5" y1="12" x2="19" y2="12" />
              </svg>
              New Decision Room
            </button>
            <button
              type="button"
              className="back-to-dashboard"
              onClick={() => setPhase('dashboard')}
            >
              ← Back to dashboard
            </button>
          </div>
        </div>

        {/* Stat tiles — only render when we have spaces (no point
            staring at zeros while loading or in the empty state). */}
        {!loading && !error && spaces.length > 0 && (
          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
            gap: 12, marginBottom: 32,
          }}>
            <StatTile label="Total spaces" value={spaces.length} />
            <StatTile label="Open" value={summary.open} accent="#16a34a" />
            <StatTile label="Trailers in spaces" value={summary.totalReels} />
            <StatTile label="Active share links" value={summary.totalDists} />
          </div>
        )}

        {loading && (
          <p style={{ color: 'var(--room-text-muted)' }}>Loading your Decision Rooms…</p>
        )}

        {error && (
          <div style={{
            padding: '16px 20px', borderRadius: 12, background: 'rgba(239, 68, 68, 0.08)',
            border: '1px solid rgba(239, 68, 68, 0.3)', color: '#dc2626', marginBottom: 16,
          }}>
            <strong>Couldn't load Decision Rooms:</strong> {error}
          </div>
        )}

        {!loading && !error && spaces.length === 0 && (
          <div style={{
            padding: '64px 32px', borderRadius: 20, background: 'var(--room-card)',
            border: '1px dashed var(--room-border, #cbd5e1)', textAlign: 'center',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.04)',
          }}>
            <h3 style={{ margin: '0 0 8px', fontSize: 18, fontWeight: 600 }}>No Decision Rooms yet</h3>
            <p style={{ margin: '0 0 20px', color: 'var(--room-text-muted)', fontSize: 14 }}>
              A Decision Room is one branded workspace per deal. Create one from a
              qualifying call, or start a fresh room here and add trailers, documents,
              and stakeholders as the deal moves.
            </p>
            <button
              type="button"
              onClick={() => setCreateOpen(true)}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 8,
                padding: '10px 18px', borderRadius: 10, border: 'none',
                background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer',
                fontFamily: 'inherit', boxShadow: '0 2px 8px rgba(124, 58, 237, 0.3)',
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <line x1="12" y1="5" x2="12" y2="19" />
                <line x1="5" y1="12" x2="19" y2="12" />
              </svg>
              New Decision Room
            </button>
          </div>
        )}

        {!loading && !error && spaces.length > 0 && (
          // Card grid — two columns on wide screens, one on narrow.
          // Each card pulls a colored stripe from a deterministic
          // palette tied to the prospect name so the page feels lived-in
          // rather than a uniform white wall.
          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(360px, 1fr))',
            gap: 16,
          }}>
            {spaces.map((s) => <SpaceCard key={s.id} space={s} onOpen={() => setSelectedSpaceId(s.id)} />)}
          </div>
        )}

        <p style={{ marginTop: 40, fontSize: 12, color: 'var(--room-text-subtle)', textAlign: 'center' }}>
          Decision Rooms is in early access. Documents upload, conversations embed, and the prospect view ship as the seller side fills in.
        </p>
      </div>

      {createOpen && (
        <CreateSpaceModal
          authToken={authToken}
          onClose={() => setCreateOpen(false)}
          onCreated={handleCreated}
        />
      )}
    </div>
  );
}

// ─── Sub-components for the list view restyle ──────────────────

function StatTile({ label, value, accent = '#7c3aed' }) {
  return (
    <div style={{
      padding: '14px 18px', borderRadius: 12, background: 'var(--room-card)',
      border: '1px solid var(--room-border)', boxShadow: '0 1px 3px rgba(15, 23, 42, 0.04)',
    }}>
      <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--room-text-muted)' }}>
        {label}
      </div>
      <div style={{ fontSize: 26, fontWeight: 700, color: accent, marginTop: 4 }}>
        {value}
      </div>
    </div>
  );
}

// Deterministic accent color per Space — same prospect name always
// renders the same stripe so the page reads as predictable + alive.
// djb2 hash → palette index. Palette is intentionally muted so the
// stripes don't fight the rest of the UI.
const STRIPE_PALETTE = [
  '#a855f7', '#6366f1', '#0ea5e9', '#22c55e',
  '#f59e0b', '#ec4899', '#14b8a6', '#ef4444',
];
function stripeFor(name) {
  let h = 5381;
  for (const ch of String(name || 'TC')) h = ((h << 5) + h + ch.charCodeAt(0)) | 0;
  return STRIPE_PALETTE[Math.abs(h) % STRIPE_PALETTE.length];
}
function initialsFor(name) {
  return String(name || 'TC').split(/\s+/).filter(Boolean).slice(0, 2)
    .map((w) => w[0]).join('').toUpperCase() || 'TC';
}

function SpaceCard({ space, onOpen }) {
  const stripe = stripeFor(space.prospectCompanyName);
  const initials = initialsFor(space.prospectCompanyName);
  return (
    <div
      onClick={onOpen}
      style={{
        background: 'var(--room-card)', borderRadius: 16,
        border: '1px solid var(--room-border)', cursor: 'pointer',
        overflow: 'hidden',
        transition: 'transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease',
        display: 'flex', flexDirection: 'column',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-3px)';
        e.currentTarget.style.boxShadow = `0 12px 32px ${stripe}1f, 0 1px 3px rgba(15, 23, 42, 0.04)`;
        e.currentTarget.style.borderColor = stripe;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = 'none';
        e.currentTarget.style.borderColor = 'var(--room-border, #e2e8f0)';
      }}
    >
      {/* Stripe + identity row. The colored bar is the page's only
          per-card visual variation; everything else stays consistent
          so a sea of cards reads predictably. */}
      <div style={{ height: 6, background: `linear-gradient(90deg, ${stripe} 0%, ${stripe}cc 100%)` }} />
      <div style={{ padding: '20px 22px', display: 'flex', flexDirection: 'column', gap: 14, flex: 1 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
          {/* Initials avatar — fast visual anchor for scanning the
              grid. Same color as the top stripe so it reads as one
              piece of identity. */}
          <div
            aria-hidden="true"
            style={{
              width: 44, height: 44, borderRadius: 10,
              background: `${stripe}1a`, color: stripe,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 15, fontWeight: 700, flex: 'none',
            }}
          >
            {initials}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: 'var(--room-text-strong)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {space.prospectCompanyName}
              </h3>
              {statusChip(space.status)}
              {/* Owner badge — only renders for admins viewing a Space
                  they don't own. Per cluster B of the 2026-04-27
                  security review: admins see every Space in the company,
                  badge tells them whose deal each row is. Owned-by-self
                  rows skip the badge to keep the grid clean. NULL
                  ownerName (orphaned after hard-delete) shows
                  "Orphaned" so admins know to transfer. */}
              {!space.ownedByCaller && (
                <span style={{
                  fontSize: 11, fontWeight: 600,
                  padding: '2px 8px', borderRadius: 100,
                  background: space.ownerName ? 'var(--room-border-soft, #f1f5f9)' : '#fef3c7',
                  color: space.ownerName ? 'var(--room-text-body, #475569)' : '#92400e',
                  border: `1px solid ${space.ownerName ? 'var(--room-border, #e2e8f0)' : '#fde68a'}`,
                  whiteSpace: 'nowrap',
                }}>
                  {space.ownerName ? `Owned by ${space.ownerName}` : 'Orphaned — needs transfer'}
                </span>
              )}
            </div>
            {space.primaryContactEmail && (
              <div style={{ fontSize: 13, color: 'var(--room-text-subtle)', marginTop: 4 }}>
                {space.primaryContactEmail}
              </div>
            )}
          </div>
        </div>

        {/* Mini-stats row */}
        <div style={{
          display: 'flex', gap: 20, fontSize: 13, color: 'var(--room-text-muted)',
          paddingTop: 10, borderTop: '1px solid var(--room-border-soft, #f1f5f9)',
        }}>
          <span><strong style={{ color: 'var(--room-text-strong)', fontSize: 15 }}>{space.reelCount}</strong> trailer{space.reelCount === 1 ? '' : 's'}</span>
          <span><strong style={{ color: 'var(--room-text-strong)', fontSize: 15 }}>{space.distributionCount}</strong> share link{space.distributionCount === 1 ? '' : 's'}</span>
          <span style={{ marginLeft: 'auto', color: 'var(--room-text-subtle)' }}>
            Active {fmtRelative(space.lastActivityAt)}
          </span>
        </div>
      </div>
    </div>
  );
}

// ─── Create-a-room modal ───────────────────────────────────────────
// Standalone creation (no qualifying call). POSTs to /api/buyer-spaces
// and hands the created room back to the parent, which jumps into its
// detail view. Only the prospect company name is required; contact
// email is optional sugar so the room header isn't empty.
function CreateSpaceModal({ authToken, onClose, onCreated }) {
  const [companyName, setCompanyName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Close on Escape — matches the rest of the app's modal behavior.
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && !submitting) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [submitting, onClose]);

  const submit = async () => {
    const name = companyName.trim();
    if (!name) { setError('Enter the prospect company name.'); return; }
    setSubmitting(true);
    setError(null);
    try {
      const resp = await fetch(`${API_BASE}/api/buyer-spaces`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
        body: JSON.stringify({
          prospectCompanyName: name,
          primaryContactEmail: contactEmail.trim() || undefined,
        }),
      });
      const body = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(body.error || `Create failed (${resp.status})`);
      onCreated(body.buyerSpace);
    } catch (err) {
      setError(err.message);
      setSubmitting(false);
    }
  };

  return (
    <div
      onClick={() => { if (!submitting) onClose(); }}
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        background: 'rgba(15, 23, 42, 0.55)', backdropFilter: 'blur(2px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Create a new Decision Room"
        style={{
          background: 'var(--room-card)', borderRadius: 16, width: '100%', maxWidth: 460,
          border: '1px solid var(--room-border)', boxShadow: '0 24px 60px rgba(15, 23, 42, 0.3)',
          padding: 28,
        }}
      >
        <h2 style={{ margin: '0 0 6px', fontSize: 20, fontWeight: 700, color: 'var(--room-text-strong)' }}>
          New Decision Room
        </h2>
        <p style={{ margin: '0 0 20px', fontSize: 13, color: 'var(--room-text-muted)' }}>
          One branded workspace for this deal. You can add trailers, documents, and
          stakeholders once it's created.
        </p>

        <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--room-text-muted)', marginBottom: 6 }}>
          Prospect company name <span style={{ color: '#dc2626' }}>*</span>
        </label>
        <input
          autoFocus
          value={companyName}
          onChange={(e) => setCompanyName(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') submit(); }}
          placeholder="Acme Logistics"
          maxLength={200}
          style={{
            width: '100%', padding: '10px 12px', borderRadius: 8,
            border: '1px solid var(--room-border)', fontSize: 14, fontFamily: 'inherit',
            boxSizing: 'border-box', marginBottom: 16,
          }}
        />

        <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--room-text-muted)', marginBottom: 6 }}>
          Primary contact email <span style={{ fontWeight: 400 }}>(optional)</span>
        </label>
        <input
          type="email"
          value={contactEmail}
          onChange={(e) => setContactEmail(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') submit(); }}
          placeholder="buyer@acme.com"
          style={{
            width: '100%', padding: '10px 12px', borderRadius: 8,
            border: '1px solid var(--room-border)', fontSize: 14, fontFamily: 'inherit',
            boxSizing: 'border-box', marginBottom: error ? 10 : 20,
          }}
        />

        {error && (
          <div style={{ fontSize: 13, color: '#dc2626', marginBottom: 16 }}>{error}</div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            style={{
              padding: '10px 16px', borderRadius: 8, border: '1px solid var(--room-border)',
              background: 'var(--room-card)', color: 'var(--room-text-strong)',
              fontSize: 14, fontWeight: 500, cursor: submitting ? 'not-allowed' : 'pointer',
              fontFamily: 'inherit',
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={submitting}
            style={{
              padding: '10px 18px', borderRadius: 8, border: 'none',
              background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
              color: '#fff', fontSize: 14, fontWeight: 600,
              cursor: submitting ? 'wait' : 'pointer', fontFamily: 'inherit',
              opacity: submitting ? 0.7 : 1,
            }}
          >
            {submitting ? 'Creating…' : 'Create room'}
          </button>
        </div>
      </div>
    </div>
  );
}
